import React, { useState, useEffect } from 'react';
import LocationCard from './LocationCard';
import config from 'src/config';

interface GeocodedLocationCardProps {
	title: string;
	type: 'location' | 'service-area';
	// For radius type service areas
	address?: {
		street?: string;
		city?: string;
		state?: string;
		postalCode?: string;
		country?: string;
		lat?: number | string;
		long?: number | string;
	};
	radius?: number;
	// For geographic type service areas
	geographic?: {
		entityType?: 'neighborhood' | 'city' | 'county' | 'state' | 'country';
		entityName?: string;
		country?: string;
		state?: string | null;
		displayName?: string;
		lat?: number | null;
		lng?: number | null;
	};
	serviceAreaType?: 'radius' | 'geographic';
	// Actions
	onEdit?: () => void;
	onRemove?: () => void;
	onClick?: () => void;
	isSelected?: boolean;
	height?: string;
	maxWidth?: string;
}

/**
 * A LocationCard wrapper that auto-geocodes geographic service areas
 * when coordinates are not available.
 */
const GeocodedLocationCard: React.FC<GeocodedLocationCardProps> = ({
	title,
	type,
	address,
	radius,
	geographic,
	serviceAreaType,
	onEdit,
	onRemove,
	onClick,
	isSelected = false,
	height = '200px',
	maxWidth = '100%',
}) => {
	const [geocodedCoords, setGeocodedCoords] = useState<{ lat: number; lng: number } | null>(null);
	const [isGoogleMapsLoaded, setIsGoogleMapsLoaded] = useState(false);

	// Determine if this is a geographic service area
	const isGeographic = serviceAreaType === 'geographic' || (!serviceAreaType && !!geographic);

	// Check if we already have coordinates
	const hasExistingCoords = isGeographic
		? (geographic?.lat && geographic?.lng)
		: (address?.lat && address?.long);

	// Load Google Maps API if needed
	useEffect(() => {
		// Skip if already have coords or not geographic
		if (hasExistingCoords || !isGeographic) {
			return;
		}

		const loadGoogleMaps = () => {
			if (!config.google?.mapsApiKey) {
				return;
			}

			if (window.google && window.google.maps && window.google.maps.Geocoder) {
				setIsGoogleMapsLoaded(true);
				return;
			}

			const existingScript = document.querySelector('script[src*="maps.googleapis.com"]');
			if (existingScript) {
				let retryCount = 0;
				const checkLoaded = () => {
					if (window.google && window.google.maps && window.google.maps.Geocoder) {
						setIsGoogleMapsLoaded(true);
					} else if (retryCount < 50) {
						retryCount++;
						setTimeout(checkLoaded, 100);
					}
				};
				checkLoaded();
				return;
			}

			const callbackName = `initGoogleMapsGeocode_${Date.now()}`;
			(window as any)[callbackName] = () => {
				let retryCount = 0;
				const checkApiReady = () => {
					if (window.google && window.google.maps && window.google.maps.Geocoder) {
						setIsGoogleMapsLoaded(true);
					} else if (retryCount < 50) {
						retryCount++;
						setTimeout(checkApiReady, 100);
					}
				};
				checkApiReady();
			};

			const script = document.createElement('script');
			script.src = `https://maps.googleapis.com/maps/api/js?key=${config.google.mapsApiKey}&libraries=places&loading=async&callback=${callbackName}`;
			script.async = true;
			script.defer = true;
			document.head.appendChild(script);
		};

		loadGoogleMaps();
	}, [hasExistingCoords, isGeographic]);

	// Geocode when Google Maps is loaded and we need coordinates
	useEffect(() => {
		if (!isGeographic || hasExistingCoords || geocodedCoords || !isGoogleMapsLoaded) {
			return;
		}

		const searchQuery = geographic?.displayName || geographic?.entityName || title;
		if (!searchQuery) return;

		const country = geographic?.country || 'US';
		const fullQuery = `${searchQuery}, ${country}`;

		const geocoder = new window.google.maps.Geocoder();
		geocoder.geocode({ address: fullQuery }, (results: any, status: any) => {
			if (status === 'OK' && results && results[0]) {
				const location = results[0].geometry.location;
				setGeocodedCoords({
					lat: location.lat(),
					lng: location.lng(),
				});
			}
		});
	}, [isGeographic, hasExistingCoords, geocodedCoords, isGoogleMapsLoaded, geographic, title]);

	// Build final coordinates
	let finalLat: number | string | undefined;
	let finalLong: number | string | undefined;

	if (isGeographic) {
		if (geocodedCoords) {
			finalLat = geocodedCoords.lat;
			finalLong = geocodedCoords.lng;
		} else if (geographic?.lat && geographic?.lng) {
			finalLat = geographic.lat;
			finalLong = geographic.lng;
		}
	} else {
		finalLat = address?.lat;
		finalLong = address?.long;
	}

	// Build address object for LocationCard
	const finalAddress = {
		street: isGeographic ? undefined : address?.street,
		city: isGeographic ? geographic?.entityName : address?.city,
		state: isGeographic ? (geographic?.state || undefined) : address?.state,
		postalCode: isGeographic ? undefined : address?.postalCode,
		country: isGeographic ? geographic?.country : address?.country,
		lat: finalLat,
		long: finalLong,
	};

	return (
		<LocationCard
			title={title}
			address={finalAddress}
			type={type}
			radius={isGeographic ? undefined : radius}
			entityType={isGeographic ? geographic?.entityType : undefined}
			onEdit={onEdit}
			onRemove={onRemove}
			onClick={onClick}
			isSelected={isSelected}
			height={height}
			maxWidth={maxWidth}
		/>
	);
};

export default GeocodedLocationCard;

